import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import { INDIE_LAW_SHEETS } from "@/lib/sheets";
import { Sheet, Upload, Plug, AlertCircle, CheckCircle2, ExternalLink, RefreshCw, MessageSquare, Eye, EyeOff, Database, Settings2, BarChart3, Search, Key, Copy } from "lucide-react";
import HubSpotMappingModal from "@/components/HubSpotMappingModal";

function fmtSync(ts) {
  if (!ts) return "Never";
  return new Date(ts).toLocaleString();
}

function SheetCard({ id, cfg, status, schedConfig, onSynced, setMsg, onTogglePause }) {
  const [url, setUrl] = useState(schedConfig?.url || cfg.url);
  const [busy, setBusy] = useState(false);
  const [savingUrl, setSavingUrl] = useState(false);
  const paused = schedConfig?.paused;

  useEffect(() => {
    if (schedConfig?.url) setUrl(schedConfig.url);
  }, [schedConfig?.url]);

  const sync = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(cfg.endpoint, { url });
      setMsg({ kind: "ok", text: `${cfg.label}: synced ${JSON.stringify(data)}` });
      onSynced();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsg({ kind: "err", text: typeof detail === "string" ? detail : `${cfg.label}: sync failed` });
    } finally { setBusy(false); }
  };

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      await api.post(`/integrations/sheets/${id}/url`, { url });
      setMsg({ kind: "ok", text: `${cfg.label}: URL updated.` });
      onSynced();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed to save URL" });
    } finally { setSavingUrl(false); }
  };

  const lastChangedHint = status?.last_changed_at
    ? `Changed: ${new Date(status.last_changed_at).toLocaleString()}`
    : null;
  const lastTrigger = status?.last_trigger;

  return (
    <div className={`bg-warm-tint border-t-4 ${paused ? "border-navy/40 opacity-80" : "border-yellow"} p-6`} data-testid={`indielaw-sheet-${id}`}>
      <div className="flex items-center gap-3">
        <Sheet className="w-5 h-5 text-navy" />
        <h3 className="text-lg font-bold text-navy">{cfg.label}</h3>
        <span className={`ml-auto text-[10px] uppercase tracking-widest ${paused ? "text-midnight opacity-60" : "text-evergreen"}`} data-testid={`indielaw-${id}-state`}>
          {paused ? "Paused" : "Auto · 6h"}
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">{cfg.description}</p>
      <div className="mt-2 text-xs text-cobalt italic">Drives: {cfg.drives}</div>
      <div className="mt-4">
        <label className="block text-[10px] uppercase tracking-widest opacity-70 mb-1">Sheet URL</label>
        <div className="flex">
          <input value={url} onChange={(e) => setUrl(e.target.value)}
                 data-testid={`indielaw-${id}-url`}
                 className="flex-1 border border-navy/30 bg-white px-3 py-2 text-sm" />
          <a href={url} target="_blank" rel="noopener noreferrer"
             data-testid={`indielaw-${id}-open`}
             className="px-3 border border-l-0 border-navy/30 bg-navy text-warm inline-flex items-center">
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        {url !== (schedConfig?.url || cfg.url) && (
          <button onClick={saveUrl} disabled={savingUrl} className="mt-2 text-xs underline text-cobalt"
                  data-testid={`indielaw-${id}-save-url`}>
            {savingUrl ? "Saving…" : "Save this URL as the auto-sync target"}
          </button>
        )}
      </div>
      <div className="mt-4 flex gap-3 items-center flex-wrap">
        <button onClick={sync} disabled={busy} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                data-testid={`indielaw-${id}-sync`}>
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
        </button>
        <button onClick={() => onTogglePause(id, !paused)}
                data-testid={`indielaw-${id}-pause`}
                className="text-xs text-cobalt underline">
          {paused ? "Resume auto-sync" : "Pause auto-sync"}
        </button>
        <div className="text-xs opacity-60 ml-auto text-right">
          <div>Synced: {fmtSync(status?.last_synced)} {lastTrigger && <span className="opacity-70">· {lastTrigger}</span>}</div>
          {lastChangedHint && <div className="text-evergreen">{lastChangedHint}</div>}
        </div>
      </div>
      {status?.last_error && (
        <div className="mt-3 text-xs text-red-700">Last error: {status.last_error}</div>
      )}
    </div>
  );
}

function SlackCard({ status, schedConfig, onSynced, setMsg, onTogglePause }) {
  const [busy, setBusy] = useState(false);
  const paused = schedConfig?.paused;

  const sync = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/slack/run-now");
      if (data?.ok) {
        setMsg({ kind: "ok", text: `Daily Highlights (Slack): synced as-of post ${data.message_ts}` });
      } else {
        setMsg({ kind: "err", text: data?.error || "Slack sync did not complete" });
      }
      onSynced();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Slack sync failed" });
    } finally { setBusy(false); }
  };

  const lastChangedHint = status?.last_changed_at
    ? `Changed: ${new Date(status.last_changed_at).toLocaleString()}`
    : null;
  const lastTrigger = status?.last_trigger;

  return (
    <div className={`bg-warm-tint border-t-4 ${paused ? "border-navy/40 opacity-80" : "border-yellow"} p-6`} data-testid="indielaw-sheet-slack_daily_highlights">
      <div className="flex items-center gap-3">
        <MessageSquare className="w-5 h-5 text-navy" />
        <h3 className="text-lg font-bold text-navy">Daily Highlights (Slack)</h3>
        <span className={`ml-auto text-[10px] uppercase tracking-widest ${paused ? "text-midnight opacity-60" : "text-evergreen"}`} data-testid="indielaw-slack-state">
          {paused ? "Paused" : "Auto · 6h"}
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">
        Reads the most recent <strong>#daily-highlights</strong> post each sync. Per the REF tab, these are the canonical daily source for Calls Booked, Calls Had, Show-Up %, Qualified Calls, and Sales — when the sheet disagrees, this wins.
      </p>
      <div className="mt-2 text-xs text-cobalt italic">Drives: reconciliation flags on Dashboard, Targets, and Recovery.</div>
      <div className="mt-4 flex gap-3 items-center flex-wrap">
        <button onClick={sync} disabled={busy} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="indielaw-slack-sync">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
        </button>
        <button onClick={() => onTogglePause("slack_daily_highlights", !paused)}
                data-testid="indielaw-slack-pause"
                className="text-xs text-cobalt underline">
          {paused ? "Resume auto-sync" : "Pause auto-sync"}
        </button>
        <div className="text-xs opacity-60 ml-auto text-right">
          <div>Synced: {fmtSync(status?.last_synced)} {lastTrigger && <span className="opacity-70">· {lastTrigger}</span>}</div>
          {lastChangedHint && <div className="text-evergreen">{lastChangedHint}</div>}
        </div>
      </div>
      {status?.last_error && (
        <div className="mt-3 text-xs text-red-700">Last error: {status.last_error}</div>
      )}
    </div>
  );
}

function GoogleAdsCard({ status, schedConfig, onSynced, setMsg, onTogglePause }) {
  const [busy, setBusy] = useState(false);
  const paused = schedConfig?.paused;
  const connected = !!status?.connected;

  const sync = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/google-ads/sync/run-now");
      if (data?.ok) {
        setMsg({ kind: "ok", text: `Google Ads: synced ${data.synced} campaign-day rows (last ${data.lookback_days}d).` });
      } else {
        setMsg({ kind: "err", text: data?.error || "Google Ads sync did not complete." });
      }
      onSynced();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Google Ads sync failed" });
    } finally { setBusy(false); }
  };

  const sync_meta = status?.sync || {};
  const lastChangedHint = sync_meta?.last_changed_at
    ? `Changed: ${new Date(sync_meta.last_changed_at).toLocaleString()}`
    : null;

  return (
    <div className={`bg-warm-tint border-t-4 ${paused ? "border-navy/40 opacity-80" : (connected ? "border-evergreen" : "border-cobalt")} p-6`}
         data-testid="indielaw-sheet-google_ads_api">
      <div className="flex items-center gap-3">
        <BarChart3 className="w-5 h-5 text-navy" />
        <h3 className="text-lg font-bold text-navy">Google Ads (Direct API)</h3>
        <span className={`ml-auto text-[10px] uppercase tracking-widest ${paused ? "text-midnight opacity-60" : (connected ? "text-evergreen" : "text-cobalt")}`}
              data-testid="google-ads-state">
          {paused ? "Paused" : (connected ? "Connected · Auto 6h" : "Not connected")}
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">
        Direct read-only pull of per-campaign daily performance (spend, impressions, clicks, conversions, CPL) for the last <strong>{status?.lookback_days || 90} days</strong>. Replaces the Railtown spreadsheet as the primary Google Ads source once data is flowing.
      </p>

      {connected ? (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">Account</div>
            <div className="font-bold text-navy" data-testid="google-ads-name">{status?.descriptive_name || "—"}</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">Customer ID</div>
            <div className="font-mono text-navy" data-testid="google-ads-customer-id">{status?.customer_id || "—"}</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">Currency / TZ</div>
            <div className="text-navy">{status?.currency || "—"} · {status?.time_zone || "—"}</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">Rows in DB</div>
            <div className="font-bold text-evergreen" data-testid="google-ads-rows">{status?.rows_in_db ?? "—"}</div>
          </div>
        </div>
      ) : (
        <div className="mt-3 bg-yellow/30 border-l-4 border-yellow p-3 text-xs text-navy" data-testid="google-ads-not-connected">
          <strong>Not connected.</strong> Add the six <code>GOOGLE_ADS_*</code> variables to <code>backend/.env</code> and restart.
          {status?.error && <div className="mt-1 opacity-80">{status.error}</div>}
        </div>
      )}

      <div className="mt-4 flex gap-3 items-center flex-wrap">
        <button onClick={sync} disabled={busy || !connected} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="google-ads-sync">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
        </button>
        <button onClick={() => onTogglePause("google_ads_api", !paused)}
                disabled={!connected}
                data-testid="google-ads-pause"
                className="text-xs text-cobalt underline disabled:opacity-40">
          {paused ? "Resume auto-sync" : "Pause auto-sync"}
        </button>
        <div className="text-xs opacity-60 ml-auto text-right">
          <div>Synced: {fmtSync(sync_meta?.last_synced)} {sync_meta?.last_trigger && <span className="opacity-70">· {sync_meta.last_trigger}</span>}</div>
          {lastChangedHint && <div className="text-evergreen">{lastChangedHint}</div>}
        </div>
      </div>
      {sync_meta?.last_error && (
        <div className="mt-3 text-xs text-red-700" data-testid="google-ads-last-error">Last error: {sync_meta.last_error}</div>
      )}
    </div>
  );
}

function HubSpotCard({ status, schedConfig, onChanged, setMsg, onTogglePause, onOpenMapping }) {
  const [token, setToken] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const connected = !!status?.connected;
  const mappingDone = !!status?.mapping_confirmed_at;
  const isPrimary = !!status?.is_primary;
  const scopesMissing = (status?.scopes_missing || []);
  const paused = schedConfig?.paused;

  const connect = async () => {
    if (!token.trim()) { setMsg({ kind: "err", text: "Paste your HubSpot Private App token first." }); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/hubspot/connect", { token: token.trim() });
      if (data?.connected && !data.scopes_missing?.length) {
        setMsg({ kind: "ok", text: `HubSpot connected. ${data.scopes_present?.length || 0} scopes verified.` });
        setToken("");
        setShowRotate(false);
      } else if (data?.scopes_missing?.length) {
        setMsg({ kind: "err", text: `Token valid but missing scopes: ${data.scopes_missing.join(", ")}` });
      } else {
        setMsg({ kind: "err", text: data?.last_error || "Token verification failed." });
      }
      onChanged();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Connection failed." });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!confirmDisconnect) {
      setConfirmDisconnect(true);
      // auto-cancel after 5s if not confirmed
      setTimeout(() => setConfirmDisconnect(false), 5000);
      return;
    }
    setBusy(true); setMsg(null);
    try {
      await api.post("/integrations/hubspot/disconnect");
      setMsg({ kind: "ok", text: "HubSpot disconnected. Mapping is preserved — paste a fresh token to reconnect." });
      setConfirmDisconnect(false);
      onChanged();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Disconnect failed" });
    } finally {
      setBusy(false);
    }
  };

  const runSync = async () => {
    setSyncBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/hubspot/sync/run-now");
      if (data?.ok) setMsg({ kind: "ok", text: `HubSpot sync complete · ${data.synced} deals` });
      else if (data?.skipped) setMsg({ kind: "err", text: `Sync skipped (${data.reason})` });
      else setMsg({ kind: "err", text: data?.error || "Sync failed" });
      onChanged();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Sync failed" });
    } finally { setSyncBusy(false); }
  };

  const lastSynced = status?.sync?.last_synced;
  const lastErr = status?.last_error || status?.sync?.last_error;
  const dealCount = status?.sync?.deals_count_last_sync;
  const meetingCount = status?.sync?.meetings_count_last_sync;
  const meetingsSource = status?.sync?.meetings_source;
  const presentScopes = status?.scopes_present || [];
  const hasMeetingsScope = presentScopes.includes("crm.objects.meetings.read")
                        || presentScopes.includes("crm.objects.appointments.read");

  return (
    <div className={`bg-warm-tint border-t-4 ${connected && mappingDone ? "border-evergreen" : "border-cobalt"} p-6`} data-testid="indielaw-sheet-hubspot_deals">
      <div className="flex items-center gap-3">
        <Database className="w-5 h-5 text-cobalt" />
        <h3 className="text-lg font-bold text-navy">HubSpot (Native CRM)</h3>
        <span className={`ml-auto text-[10px] uppercase tracking-widest ${connected ? (isPrimary ? "text-evergreen" : "text-cobalt") : "text-midnight opacity-50"}`} data-testid="hubspot-state">
          {!connected ? "Not connected" : isPrimary ? "Primary source · live" : mappingDone ? "Connected · read-only" : "Connected · mapping pending"}
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">
        Native HubSpot Private App. Pulls Deals + stage + source + owner every 6h. Once mapping is confirmed and activated, the <strong>Leads page and Recovery funnel</strong> source switch from Sheets to HubSpot. Sheets continue to sync as a reconciliation backup.
      </p>

      {!connected && (
        <div className="mt-4 space-y-2">
          <label className="text-[10px] uppercase tracking-widest text-cobalt block">Private App Access Token</label>
          <div className="flex gap-2">
            <input type={show ? "text" : "password"} value={token} onChange={(e) => setToken(e.target.value)}
                   placeholder="pat-na1-…"
                   className="flex-1 border-2 border-navy/30 px-3 py-2 text-sm bg-white font-mono text-navy"
                   data-testid="hubspot-token-input" />
            <button onClick={() => setShow((s) => !s)} className="border-2 border-navy/30 px-2"
                    data-testid="hubspot-token-toggle">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button onClick={connect} disabled={busy} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="hubspot-connect">
              <Plug className="w-4 h-4" /> {busy ? "Verifying…" : "Connect & test"}
            </button>
          </div>
          <p className="text-[11px] opacity-60">Stored server-side only. Never sent to the browser after save.</p>
        </div>
      )}

      {connected && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Token</div>
              <div className="font-mono text-navy" data-testid="hubspot-token-masked">{status?.token_masked || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Portal</div>
              <div className="text-navy">{status?.portal_id || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Scopes</div>
              <div className={scopesMissing.length ? "text-red-700" : "text-evergreen"}>
                {scopesMissing.length ? `Missing ${scopesMissing.length}` : `All 4 ✓`}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Last sync</div>
              <div className="text-navy">
                {fmtSync(lastSynced)}
                {dealCount != null && <span className="opacity-60"> · {dealCount} deals</span>}
                {meetingCount != null && <span className="opacity-60"> · {meetingCount} {meetingsSource || "meetings"}</span>}
              </div>
            </div>
          </div>

          <div className="border-t-2 border-navy/10 pt-3">
            {!showRotate ? (
              <button onClick={() => setShowRotate(true)}
                      className="text-xs text-cobalt underline"
                      data-testid="hubspot-rotate-toggle">
                Rotate access token →
              </button>
            ) : (
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-cobalt block">New Private App Access Token</label>
                <div className="flex gap-2">
                  <input type={show ? "text" : "password"} value={token} onChange={(e) => setToken(e.target.value)}
                         placeholder="pat-na1-…"
                         className="flex-1 border-2 border-navy/30 px-3 py-2 text-sm bg-white font-mono text-navy"
                         data-testid="hubspot-rotate-input" />
                  <button onClick={() => setShow((s) => !s)} className="border-2 border-navy/30 px-2"
                          data-testid="hubspot-rotate-toggle-show">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={connect} disabled={busy} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                          data-testid="hubspot-rotate-save">
                    <Plug className="w-4 h-4" /> {busy ? "Verifying…" : "Save new token"}
                  </button>
                  <button onClick={() => { setShowRotate(false); setToken(""); }}
                          className="text-xs text-navy underline"
                          data-testid="hubspot-rotate-cancel">
                    Cancel
                  </button>
                </div>
                <p className="text-[11px] opacity-60">Replaces the stored token in place — mapping, schedule, and sync history are preserved.</p>
              </div>
            )}
          </div>

          {!hasMeetingsScope && (
            <div className="bg-yellow/30 border-l-4 border-yellow p-2 text-xs text-navy" data-testid="hubspot-meetings-scope-missing">
              <strong>Calls/meetings scope not yet present.</strong> Add either <code>crm.objects.appointments.read</code> (newer accounts) <em>or</em> <code>crm.objects.meetings.read</code> (legacy) to your Private App, click <em>Reinstall to workspace</em>, then re-paste the token here.
            </div>
          )}

          {scopesMissing.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-600 p-2 text-xs text-red-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>Missing required scopes: <code>{scopesMissing.join(", ")}</code>. Add these in your Private App&rsquo;s Scopes tab and click &ldquo;Reinstall to workspace,&rdquo; then re-paste the token.</div>
            </div>
          )}

          {lastErr && (
            <div className="bg-red-50 border-l-4 border-red-600 p-2 text-xs text-red-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {lastErr}
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={onOpenMapping} className="btn-cobalt inline-flex items-center gap-2"
                    data-testid="hubspot-open-mapping">
              <Settings2 className="w-4 h-4" /> {mappingDone ? "Edit field mapping" : "Configure field mapping →"}
            </button>
            <button onClick={runSync} disabled={syncBusy || scopesMissing.length > 0} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="hubspot-sync">
              <RefreshCw className={`w-4 h-4 ${syncBusy ? "animate-spin" : ""}`} /> {syncBusy ? "Syncing…" : "Sync now"}
            </button>
            <button onClick={() => onTogglePause("hubspot_deals", !paused)}
                    data-testid="hubspot-pause"
                    className="text-xs text-cobalt underline">
              {paused ? "Resume auto-sync" : "Pause auto-sync"}
            </button>
            <button onClick={disconnect} disabled={busy}
                    className={`text-xs underline ml-auto disabled:opacity-50 ${confirmDisconnect ? "text-yellow bg-navy px-2 py-1 font-bold animate-pulse" : "text-red-700"}`}
                    data-testid="hubspot-disconnect">
              {confirmDisconnect ? "Click again to confirm disconnect" : "Disconnect"}
            </button>
          </div>

          {connected && !mappingDone && (
            <div className="bg-yellow/20 border-l-4 border-yellow p-2 text-xs text-navy">
              <strong>Next step:</strong> open &ldquo;Configure field mapping&rdquo; above to review the stage &rarr; funnel and source mappings. Sync stays disabled until you save it.
            </div>
          )}
          {mappingDone && !isPrimary && (
            <div className="bg-cobalt/10 border-l-4 border-cobalt p-2 text-xs text-navy">
              Mapping saved · HubSpot syncs every 6h but Leads/Recovery still read from Sheets. Open mapping again and click <em>Save &amp; activate as primary</em> when you&rsquo;re ready to flip the switch.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GoogleSEOCard({ status, schedConfig, onChanged, setMsg, onTogglePause }) {
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const paused = schedConfig?.paused;
  const connected = !!status?.connected;
  const ok = !!status?.ok;

  const connect = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.get("/integrations/google/oauth-start");
      if (data?.url) {
        // Same-tab redirect — Google bounces back to /integrations on success
        window.location.href = data.url;
      } else {
        setMsg({ kind: "err", text: "Could not start Google OAuth flow." });
      }
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "OAuth start failed." });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!confirmDisconnect) { setConfirmDisconnect(true); setTimeout(() => setConfirmDisconnect(false), 4000); return; }
    setBusy(true); setMsg(null);
    try {
      await api.post("/integrations/google/disconnect");
      setMsg({ kind: "ok", text: "Disconnected Google Search Console + GA4." });
      onChanged();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Disconnect failed." });
    } finally { setBusy(false); setConfirmDisconnect(false); }
  };

  const sync = async () => {
    setSyncBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/google-seo/sync/run-now");
      if (data?.skipped) {
        setMsg({ kind: "err", text: `Skipped: ${data.reason}` });
      } else if (data?.ok) {
        setMsg({ kind: "ok", text: `GSC + GA4 synced — GSC: ${data.gsc_synced} rows · GA4: ${data.ga4_synced} days.` });
      } else {
        setMsg({ kind: "err", text: (data?.errors || []).join("; ") || "Sync had errors." });
      }
      onChanged();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Sync failed." });
    } finally { setSyncBusy(false); }
  };

  const sync_meta = status?.sync || {};

  return (
    <div className={`bg-warm-tint border-t-4 ${paused ? "border-navy/40 opacity-80" : (connected && ok ? "border-evergreen" : connected ? "border-yellow" : "border-cobalt")} p-6`}
         data-testid="indielaw-sheet-gsc_ga4">
      <div className="flex items-center gap-3">
        <Search className="w-5 h-5 text-navy" />
        <h3 className="text-lg font-bold text-navy">Google Search Console + GA4</h3>
        <span className={`ml-auto text-[10px] uppercase tracking-widest ${paused ? "text-midnight opacity-60" : (connected && ok ? "text-evergreen" : connected ? "text-orange-700" : "text-cobalt")}`}
              data-testid="google-seo-state">
          {paused ? "Paused" : (connected && ok ? "Connected · Auto 24h" : connected ? "Connected · needs attention" : "Not connected")}
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">
        Pulls organic keyword rankings (clicks, impressions, CTR, position) from <strong>Search Console</strong> and organic-search traffic (sessions, users, conversions) from <strong>GA4</strong>. One Google sign-in covers both.
      </p>
      <div className="mt-2 text-xs text-cobalt italic">Drives: SEO &rarr; Keywords &amp; Organic Traffic pages.</div>

      {connected ? (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">Connected as</div>
            <div className="font-bold text-navy break-all" data-testid="google-seo-email">{status?.connected_email || "—"}</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">GSC site</div>
            <div className="font-mono text-navy text-[11px] break-all" data-testid="google-seo-site">{status?.site_url || "—"}</div>
            <div className={`text-[10px] ${status?.gsc_ok ? "text-evergreen" : "text-red-700"}`}>{status?.gsc_ok ? "✓ verified" : "✗ check property"}</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">GA4 property</div>
            <div className="font-mono text-navy" data-testid="google-seo-property">{status?.property_id || "—"}</div>
            <div className={`text-[10px] ${status?.ga4_ok ? "text-evergreen" : "text-red-700"}`}>{status?.ga4_ok ? "✓ reachable" : "✗ check access"}</div>
          </div>
          <div>
            <div className="opacity-60 uppercase tracking-widest text-[10px]">Rows in DB</div>
            <div className="font-bold text-evergreen" data-testid="google-seo-rows">
              GSC {status?.gsc_rows_in_db ?? 0} · GA4 {status?.ga4_rows_in_db ?? 0}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3 bg-yellow/30 border-l-4 border-yellow p-3 text-xs text-navy" data-testid="google-seo-not-connected">
          <strong>Not connected.</strong> One Google sign-in grants read-only access to your Search Console &amp; GA4 properties. We never write — only fetch metrics.
          {!status?.configured && (
            <div className="mt-1">
              <strong>Setup needed:</strong> add <code>GOOGLE_ADS_CLIENT_ID</code> and <code>GOOGLE_ADS_CLIENT_SECRET</code> to <code>backend/.env</code> (already there if Google Ads is connected).
            </div>
          )}
        </div>
      )}

      {(status?.errors || []).length > 0 && (
        <div className="mt-3 text-xs text-red-700" data-testid="google-seo-errors">
          {status.errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      <div className="mt-4 flex gap-3 items-center flex-wrap">
        {connected ? (
          <>
            <button onClick={sync} disabled={syncBusy} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="google-seo-sync">
              <RefreshCw className={`w-4 h-4 ${syncBusy ? "animate-spin" : ""}`} /> {syncBusy ? "Syncing…" : "Sync now"}
            </button>
            <button onClick={() => onTogglePause("gsc_ga4", !paused)}
                    data-testid="google-seo-pause"
                    className="text-xs text-cobalt underline">
              {paused ? "Resume auto-sync" : "Pause auto-sync"}
            </button>
            <button onClick={disconnect} disabled={busy}
                    className={`text-xs underline ml-auto disabled:opacity-50 ${confirmDisconnect ? "text-yellow bg-navy px-2 py-1 font-bold animate-pulse" : "text-red-700"}`}
                    data-testid="google-seo-disconnect">
              {confirmDisconnect ? "Click again to confirm disconnect" : "Disconnect"}
            </button>
          </>
        ) : (
          <button onClick={connect} disabled={busy || !status?.configured}
                  className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="google-seo-connect">
            <Plug className="w-4 h-4" /> {busy ? "Opening Google…" : "Connect Google"}
          </button>
        )}
        <div className="text-xs opacity-60 ml-auto text-right">
          <div>Synced: {fmtSync(sync_meta?.last_synced)} {sync_meta?.last_trigger && <span className="opacity-70">· {sync_meta.last_trigger}</span>}</div>
        </div>
      </div>
      {sync_meta?.last_error && (
        <div className="mt-3 text-xs text-red-700" data-testid="google-seo-last-error">Last error: {sync_meta.last_error}</div>
      )}
    </div>
  );
}

function PublicApiCard({ setMsg }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [justCopied, setJustCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/public-api/key");
      setData(data);
    } catch (e) {
      setMsg?.({ kind: "err", text: e?.response?.data?.detail || "Could not load API key." });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = async () => {
    if (!data?.key) return;
    try {
      await navigator.clipboard.writeText(data.key);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
    } catch {
      setMsg?.({ kind: "err", text: "Copy failed — select the key and copy manually." });
    }
  };

  const rotate = async () => {
    if (!confirmRotate) {
      setConfirmRotate(true);
      setTimeout(() => setConfirmRotate(false), 4000);
      return;
    }
    setBusy(true); setMsg?.(null);
    try {
      const { data } = await api.post("/integrations/public-api/rotate");
      setData((prev) => ({ ...(prev || {}), key: data.key, rotated_at: data.rotated_at }));
      setMsg?.({ kind: "ok", text: "API key rotated. Update `MARKETING_INTEL_API_KEY` on any app using this key (e.g. Indie Forecast Pro)." });
    } catch (e) {
      setMsg?.({ kind: "err", text: e?.response?.data?.detail || "Rotate failed." });
    } finally { setBusy(false); setConfirmRotate(false); }
  };

  const key = data?.key || "";
  const masked = key ? key.slice(0, 6) + "…" + key.slice(-6) : "";
  const host = typeof window !== "undefined" ? window.location.origin : "";
  const curl = `curl -H "X-API-Key: ${key}" \\
  "${host}/api/public/marketing-summary?from=2026-04&to=2026-06"`;

  return (
    <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="public-api-card">
      <div className="flex items-center gap-3">
        <Key className="w-5 h-5 text-navy" />
        <h3 className="text-lg font-bold text-navy">Public API Key</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-cobalt">
          For Indie Forecast Pro &amp; other read-only consumers
        </span>
      </div>
      <p className="mt-2 text-sm text-midnight opacity-80">
        Read-only <code>X-API-Key</code> credential for <code>/api/public/marketing-summary</code> and <code>/api/public/channel-mix</code>. Each environment (preview / production) has its own key.
      </p>

      {loading ? (
        <div className="mt-4 text-sm opacity-60 italic">Loading key…</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
            <div className="bg-navy text-warm p-3 font-mono text-sm break-all"
                 data-testid="public-api-key-value">
              {revealed ? key : masked}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setRevealed(!revealed)}
                      className="btn-cobalt inline-flex items-center gap-2 text-xs"
                      data-testid="public-api-toggle-reveal">
                {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {revealed ? "Hide" : "Reveal"}
              </button>
              <button onClick={copy}
                      className="btn-yellow inline-flex items-center gap-2 text-xs"
                      data-testid="public-api-copy">
                <Copy className="w-3.5 h-3.5" /> {justCopied ? "Copied ✓" : "Copy"}
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-60 mb-1">Sample request</div>
            <pre className="bg-warm p-3 text-[11px] leading-relaxed overflow-x-auto text-midnight border border-navy/20"
                 data-testid="public-api-curl">{curl}</pre>
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <div className="text-xs opacity-60">
              Created: {data?.created_at ? new Date(data.created_at).toLocaleString() : "—"}
              {data?.rotated_at && <> · Rotated: {new Date(data.rotated_at).toLocaleString()}</>}
            </div>
            <button onClick={rotate} disabled={busy}
                    className={`text-xs ml-auto disabled:opacity-50 ${confirmRotate ? "text-yellow bg-navy px-2 py-1 font-bold animate-pulse" : "text-red-700 underline"}`}
                    data-testid="public-api-rotate">
              {confirmRotate ? "Click again to confirm — this invalidates the current key!" : "Rotate key"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


export default function IntegrationsPage() {
  const [status, setStatus] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [hubspotStatus, setHubspotStatus] = useState(null);
  const [hubspotModal, setHubspotModal] = useState(false);
  const [googleAdsStatus, setGoogleAdsStatus] = useState(null);
  const [googleSeoStatus, setGoogleSeoStatus] = useState(null);
  const [sheetUrl, setSheetUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef();

  const load = () => Promise.all([
    api.get("/integrations/status"),
    api.get("/integrations/schedule"),
    api.get("/integrations/hubspot/status"),
    api.get("/integrations/google-ads/status"),
    api.get("/integrations/google/status"),
  ]).then(([s, sc, hs, ga, gseo]) => {
    setStatus(s.data);
    setSchedule(sc.data);
    setHubspotStatus(hs.data);
    setGoogleAdsStatus(ga.data);
    setGoogleSeoStatus(gseo.data);
  });
  useEffect(() => {
    load();
    // Handle the OAuth callback redirect — show success/error flash
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "1") {
      setMsg({ kind: "ok", text: "Google connected. GSC + GA4 will sync within a minute — or click Sync now." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("google_error")) {
      setMsg({ kind: "err", text: `Google connect failed: ${params.get("google_error")}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const sourceStatus = (id) => status?.sources?.find((x) => x.source === id);
  const sheetCfg = (id) => schedule?.sheets?.find((x) => x.id === id);

  const togglePause = async (sheetId, paused) => {
    await api.post(`/integrations/sheets/${sheetId}/pause`, { paused });
    load();
  };

  const syncSheets = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/import/sheets", { url: sheetUrl });
      setMsg({ kind: "ok", text: `Imported ${data.inserted} rows · skipped ${data.skipped}.` });
      load();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Sheets sync failed." });
    } finally { setBusy(false); }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setMsg(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ kind: "ok", text: `Imported ${data.inserted} rows · skipped ${data.skipped}.` });
      load();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "CSV upload failed." });
    } finally { setBusy(false); fileRef.current.value = ""; }
  };

  const syncAll = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/schedule/run-now");
      const summary = Object.entries(data).map(([k, v]) =>
        `${k}:${v.ok ? "✓" : v.skipped ? "⊘" : "✗"}`).join(" · ");
      setMsg({ kind: "ok", text: `Sync all: ${summary}` });
      load();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Sync all failed." });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="integrations-page">
      <TitleBlock
        eyebrow="Integrations"
        title="Data Sources"
        subtitle="Phase 1 ingests via Sheets + CSV. Direct Google/Meta APIs are the fast-follow."
        right={
          <button onClick={syncAll} disabled={busy} className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="sync-all-btn">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Sync all Indie Law sheets
          </button>
        }
      />

      <div className="p-6 md:p-8 space-y-6">
        {msg && (
          <div className={`callout flex items-start gap-3`}
               style={msg.kind === "err" ? { borderTopColor: "#b91c1c" } : {}}
               data-testid="integrations-msg">
            {msg.kind === "err"
              ? <AlertCircle className="w-5 h-5 text-red-700 mt-0.5" />
              : <CheckCircle2 className="w-5 h-5 text-evergreen mt-0.5" />}
            <div className="text-sm">{msg.text}</div>
          </div>
        )}

        {/* Indie Law source-of-truth sheets */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt">Indie Law source-of-truth sheets</h2>
            {schedule && (
              <div className="text-xs text-midnight opacity-70" data-testid="schedule-status">
                Auto-sync every {schedule.interval_hours}h
                {schedule.next_run_at && <span> · next run {new Date(schedule.next_run_at).toLocaleTimeString()}</span>}
                {schedule.scheduler_running ? " · running" : " · stopped"}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Object.entries(INDIE_LAW_SHEETS).map(([id, cfg]) => (
              <SheetCard key={id} id={id} cfg={cfg}
                         status={sourceStatus(id)}
                         schedConfig={sheetCfg(id)}
                         onSynced={load} setMsg={setMsg}
                         onTogglePause={togglePause} />
            ))}
          </div>
        </div>

        {/* Slack reconciliation source */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt">Reconciliation source · tie-breaker</h2>
          </div>
          <SlackCard status={sourceStatus("slack_daily_highlights")}
                     schedConfig={sheetCfg("slack_daily_highlights")}
                     onSynced={load} setMsg={setMsg}
                     onTogglePause={togglePause} />
        </div>

        {/* HubSpot Native CRM */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt">CRM · native source</h2>
            <span className="text-[10px] uppercase tracking-widest text-midnight opacity-60">Replaces Leads + Recovery once activated</span>
          </div>
          <HubSpotCard status={hubspotStatus}
                       schedConfig={sheetCfg("hubspot_deals")}
                       onChanged={load} setMsg={setMsg}
                       onTogglePause={togglePause}
                       onOpenMapping={() => setHubspotModal(true)} />
        </div>

        <HubSpotMappingModal open={hubspotModal}
                             onClose={() => setHubspotModal(false)}
                             onSaved={() => { load(); }} />

        {/* Google Ads (Direct API) */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt">Paid ads · primary source</h2>
            <span className="text-[10px] uppercase tracking-widest text-midnight opacity-60">Replaces Railtown spreadsheet for Google</span>
          </div>
          <GoogleAdsCard status={googleAdsStatus}
                         schedConfig={sheetCfg("google_ads_api")}
                         onSynced={load} setMsg={setMsg}
                         onTogglePause={togglePause} />
        </div>

        {/* Google Search Console + GA4 */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt">SEO · search &amp; organic traffic</h2>
            <span className="text-[10px] uppercase tracking-widest text-midnight opacity-60">Powers Keywords + Organic Traffic pages</span>
          </div>
          <GoogleSEOCard status={googleSeoStatus}
                         schedConfig={sheetCfg("gsc_ga4")}
                         onChanged={load} setMsg={setMsg}
                         onTogglePause={togglePause} />
        </div>

        {/* Public API for external consumers (Indie Forecast Pro, etc.) */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt">Downstream consumers</h2>
            <span className="text-[10px] uppercase tracking-widest text-midnight opacity-60">Read-only API for other apps</span>
          </div>
          <PublicApiCard setMsg={setMsg} />
        </div>

        {/* Generic Sheets + CSV */}
        <div>
          <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt mb-3">Generic imports</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="integration-card-sheets">
              <div className="flex items-center gap-3">
                <Sheet className="w-5 h-5 text-navy" />
                <h3 className="text-lg font-bold text-navy">Google Sheets (Railtown)</h3>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-evergreen">Ready</span>
              </div>
              <p className="mt-2 text-sm text-midnight opacity-80">
                Paste a public Google Sheets URL with the monthly Railtown campaign export. Columns:
                date, platform, campaign, spend, impressions, clicks, leads. Deduped by campaign+date.
              </p>
              <div className="mt-4">
                <input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  data-testid="sheets-url-input"
                  className="w-full border border-navy/30 bg-white px-3 py-2 text-sm" />
                <div className="mt-3 flex gap-3">
                  <button onClick={syncSheets} disabled={busy || !sheetUrl} className="btn-yellow disabled:opacity-50"
                          data-testid="sheets-sync-btn">
                    {busy ? "Syncing…" : "Sync now"}
                  </button>
                  <div className="text-xs opacity-60 self-center">Last synced: {fmtSync(sourceStatus("sheets")?.last_synced)}</div>
                </div>
              </div>
            </div>

            <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="integration-card-csv">
              <div className="flex items-center gap-3">
                <Upload className="w-5 h-5 text-navy" />
                <h3 className="text-lg font-bold text-navy">CSV Upload</h3>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-evergreen">Ready</span>
              </div>
              <p className="mt-2 text-sm text-midnight opacity-80">
                Drag-and-drop a CSV from any vendor with date, platform, campaign, spend, impressions, clicks, leads.
              </p>
              <div className="mt-4 flex flex-wrap gap-3 items-center">
                <input type="file" accept=".csv" ref={fileRef} onChange={onFileChange}
                  data-testid="csv-file-input" className="text-sm" />
                <div className="text-xs opacity-60">Last uploaded: {fmtSync(sourceStatus("csv")?.last_synced)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Fast-follow */}
        <div>
          <h2 className="text-base uppercase tracking-[0.22em] font-bold text-cobalt mb-3">Phase 2 · fast-follow</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              ["meta_ads_api", "Meta Ads API", "First-class Meta integration so Forecast can model scaling Meta back up."],
              ["hubspot", "HubSpot Auto-attribution", "Auto-link of leads → first-touch source. Manual leads work for now."],
            ].map(([k, name, desc]) => (
              <div key={k} className="bg-warm-tint border-t-4 border-navy/40 p-6 opacity-90" data-testid={`integration-card-${k}`}>
                <div className="flex items-center gap-3">
                  <Plug className="w-5 h-5 text-navy" />
                  <h3 className="text-lg font-bold text-navy">{name}</h3>
                  <span className="ml-auto text-[10px] uppercase tracking-widest text-cobalt">Phase 2</span>
                </div>
                <p className="mt-2 text-sm text-midnight opacity-70">{desc}</p>
                <button disabled className="btn-outline-navy mt-4 opacity-50 cursor-not-allowed">Connect later</button>
              </div>
            ))}
          </div>
        </div>

        <Callout title="How sync works">
          The scheduler hits all three Indie Law sheets <strong>every {schedule?.interval_hours || 6}h</strong>. Each card shows the last synced + last changed timestamp so you can tell at a glance whether a sheet is fresh &mdash; and which one shifted overnight. Pause any source you don&rsquo;t want overwritten until you&rsquo;ve finished a manual edit upstream.
        </Callout>
      </div>
    </div>
  );
}
